import React, { useContext, useEffect } from 'react';
import { Box, Divider } from '@mui/material';

// Components
import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartRadioInput from '@component/smartForm/SmartRadioInput';

// Contexts & Hooks
import { AccountContext } from "@context/AccountProvider";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";

// Queries & Mutations
import { useAccountPostMutation } from '@mutation/accountMutation';
import { useAllAdminRolesQuery } from '@query/roleQuery';

// Services, Utils & Validators
import { mapRole, Roles } from '@service/roleService';
import { removeEmptyValues } from '@utils/generalUtils';
import { emailValidator, nameValidator, notEmptyValidator } from '@component/smartForm/fieldValidators';
import SmartContainer from './smartForm/SmartContainer';
import RoleDescriptionList from './RoleDescriptionList';

function isSiteAdminOrEmployeePicked(fetchedRoles, pickedRoleUuid) {
    for (const role of fetchedRoles || []) {
        const mappedRole = mapRole(role.apiName);
        if (role.value === pickedRoleUuid &&
            (mappedRole.level === Roles.SITE_ADMIN.level || mappedRole.level === Roles.SITE_EMPLOYEE.level)) {
            return true;
        }
    }
    return false;
}

function AccountCreateForm({ onSuccess, onCancel }) {
    const [fetchedRoles, setFetchedRoles] = React.useState([]);
    const [showSiteSelection, setShowSiteSelection] = React.useState(false);
    const [formModel, setFormModel] = React.useState({
        email: "",
        first_name: "",
        last_name: "",
        role_uuid: "",
        club_uuid: "",
        site_uuid: "",
        preferred_language: "cs"
    });

    const { sites, club } = useContext(AccountContext);
    const { addAlertBanner } = React.useContext(AlertContext);
    const { t } = useTranslation(["account", "main", "role"]);

    const accountPostMutation = useAccountPostMutation();
    const allRolesQuery = useAllAdminRolesQuery();

    useEffect(() => {
        if (allRolesQuery.data?.data) {
            // 👈 Corrected the variable name here
            const fetchedRolesClean = allRolesQuery.data.data.map((roleRecord) => ({
                value: roleRecord.uuid,
                label: t(mapRole(roleRecord.name).translationKey),
                apiName: roleRecord.name
            }));
            setFetchedRoles(fetchedRolesClean);
        }
    }, [allRolesQuery.data, t]); // 👈 Matched the dependency array from your original file

    const handleSave = (newModel) => {
        let cleanModel = removeEmptyValues(newModel);
        if (!cleanModel?.club_uuid && !cleanModel?.site_uuid) {
            cleanModel.club_uuid = club?.uuid;
        }
        accountPostMutation.mutate({ data: cleanModel }, {
            onSuccess: (response) => {
                addAlertBanner(t("main:saveSuccess"), "success");
                if (onSuccess) {
                    onSuccess(response.data?.uuid);
                }
            },
            onError: (response) => {
                if (response.status === 403) {
                    addAlertBanner(t("main:noPermissionLong"), 'error');
                } else if (response.status === 409) {
                    addAlertBanner(t("account:accountCreateConfilict"), 'error');
                } else {
                    addAlertBanner(t("main:saveError"), "error");
                }
            }
        });
    };

    useEffect(() => {
        if (fetchedRoles && formModel?.role_uuid && isSiteAdminOrEmployeePicked(fetchedRoles, formModel.role_uuid)) {
            setShowSiteSelection(true);
            setFormModel(prev => ({ ...prev, club_uuid: "" }));
        } else {
            setShowSiteSelection(false);
        }
    }, [formModel?.role_uuid, fetchedRoles]);

    return (
        <Box sx={{ px: 2, mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <SmartForm
                formModel={formModel}
                setFormModel={setFormModel}
                isEdit={true}
                onSave={handleSave}
                onCancel={onCancel}
                isLoading={accountPostMutation.isPending}
                contentSx={{ display: "flex", width: "100%" }}
                buttonPanelSx={{
                    top: "10px",
                    right: "10px",
                }}
            >
                <SmartContainer contentSx={{ display: "flex", gap: 2, width: "100%", flexDirection: "column", mt: 6 }}>
                    <SmartTextInput field="email" label={t("E-mail")} validator={emailValidator} />
                    <SmartTextInput field="first_name" label={t("main:firstName")} validator={nameValidator} />
                    <SmartTextInput field="last_name" label={t("main:lastName")} validator={nameValidator} />
                    <Divider />
                    <SmartRadioInput field="role_uuid" label={t("pickRole")} options={fetchedRoles} validator={notEmptyValidator} />
                    {showSiteSelection && <Divider />}
                    {showSiteSelection && (
                        <SmartRadioInput
                            field="site_uuid"
                            label={t("pickSite")}
                            validator={notEmptyValidator}
                            options={sites.map((site) => ({ value: site.uuid, label: site.name }))}
                        />
                    )}
                </SmartContainer>
            </SmartForm>
            <RoleDescriptionList />

        </Box>
    );
}

export default AccountCreateForm;